import { useEffect, useState, useCallback } from "react";
import { getDoneBookings } from "../../services/formService";
import Papa from "papaparse";
import "./DoneBooking.css";

const DoneBookings = () => {
  const [doneBookings, setDoneBookings] = useState([]);
  const [sortedBookings, setSortedBookings] = useState([]);
  const [sortField, setSortField] = useState("doneAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Function to fetch done bookings
  const fetchDoneBookings = useCallback(async () => {
    const data = await getDoneBookings();
    setDoneBookings(data);
  }, []);

  // Auto-fetch every 5 seconds
  useEffect(() => {
    fetchDoneBookings();
    const interval = setInterval(fetchDoneBookings, 5000);
    return () => clearInterval(interval);
  }, [fetchDoneBookings]);

  // Sorting function
  useEffect(() => {
    const sortedData = [...doneBookings].sort((a, b) => {
      return sortOrder === "asc"
        ? new Date(a[sortField]) - new Date(b[sortField])
        : new Date(b[sortField]) - new Date(a[sortField]);
    });
    setSortedBookings(sortedData);
  }, [doneBookings, sortField, sortOrder]);

  // Toggle sorting field and order
  const toggleSort = (field) => {
    setSortOrder(
      sortField === field ? (sortOrder === "asc" ? "desc" : "asc") : "asc"
    );
    setSortField(field);
  };

  // Format date function
  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatDateTime = (date) =>
    `${formatDate(date)} ${new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;

  // Export to CSV function
  const exportToCSV = () => {
    const csv = Papa.unparse(
      sortedBookings.map((booking) => ({
        Name: booking.name,
        Email: booking.email,
        Contact: booking.contactNumber,
        Address: booking.address,
        "Guest Count": booking.guestCount,
        "Total Price": `₱${booking.totalPrice.toLocaleString()}`,
        "Start Date": formatDate(booking.startDate),
        "Completed At": formatDateTime(booking.doneAt),
        "Cart Items": booking.cart
          .map(
            (item) =>
              `${item.name} - ${
                item.quantity
              } × ₱${item.price.toLocaleString()}`
          )
          .join("; "),
      }))
    );

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "completed_bookings.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="done-bookings-container">
      <h2 className="title">Completed Bookings</h2>

      <div className="sort-buttons">
        <button className="sort-button" onClick={() => toggleSort("startDate")}>
          Sort by Start Date (
          {sortOrder === "asc" && sortField === "startDate" ? "▼" : "▲"})
        </button>
        <button className="sort-button" onClick={() => toggleSort("doneAt")}>
          Sort by Completed At (
          {sortOrder === "asc" && sortField === "doneAt" ? "▼" : "▲"})
        </button>
        <button className="export-button" onClick={exportToCSV}>
          Export to CSV
        </button>
      </div>

      {sortedBookings.length === 0 ? (
        <div className="no-bookings-message">No available bookings.</div>
      ) : (
        <div className="table-wrapper">
          <table className="done-bookings-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Guest Count</th>
                <th>Total Price</th>
                <th>Start Date</th>
                <th>Completed At</th>
                <th>Cart Items</th>
              </tr>
            </thead>
            <tbody>
              {sortedBookings.map((booking) => (
                <tr key={booking._id}>
                  <td>{booking.name}</td>
                  <td>{booking.email}</td>
                  <td>{booking.contactNumber}</td>
                  <td>{booking.address}</td>
                  <td>{booking.guestCount}</td>
                  <td>₱{booking.totalPrice.toLocaleString()}</td>
                  <td>{formatDate(booking.startDate)}</td>
                  <td>{formatDateTime(booking.doneAt)}</td>
                  <td>
                    <ul>
                      {booking.cart.map((item, index) => (
                        <li key={index}>
                          <strong>{item.name}</strong> - {item.quantity} × ₱
                          {item.price.toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DoneBookings;
